import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../../../../shared';
import { BUTTON } from '../../../../../constants';

/**
 * BatchFormActions - Reusable button section for batch forms
 * Provides consistent layout and styling for form action buttons
 * Memoized to prevent unnecessary re-renders when props haven't changed
 *
 * Typical Usage Patterns:
 * - CreateBatch: Primary="Create", Secondary=hidden, Close="Close"
 * - EditBatch: Primary="Save", Secondary="Delete" (conditional), Close="Close"
 * - ReadOnly: Primary=hidden, Secondary=hidden, Close="Close"
 *
 * @param {Function} onPrimaryAction - Handler for primary action (save/create/submit)
 * @param {string} primaryLabel - Label for primary button
 * @param {Function} onSecondaryAction - Handler for secondary action (optional, e.g., delete)
 * @param {string} secondaryLabel - Label for secondary button
 * @param {Function} onClose - Handler for close button
 * @param {string} closeLabel - Label for close button (defaults to BUTTON.CLOSE)
 * @param {boolean} hidePrimary - Whether to hide primary button
 * @param {boolean} hideSecondary - Whether to hide secondary button (default: true)
 */
const BatchFormActionsComponent = ({
  onPrimaryAction,
  primaryLabel,
  onSecondaryAction,
  secondaryLabel,
  onClose,
  closeLabel = BUTTON.CLOSE,
  hidePrimary = false,
  hideSecondary = true
}) => {
  return (
    <div className="d-flex align-items-center justify-content-center gap-4 w-100 mt-4">
      {/* Primary Action Button - Main action (Create/Save/Submit) */}
      {!hidePrimary && (
        <Button
          clickHandler={onPrimaryAction}
          className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
          {primaryLabel}
        </Button>
      )}
      {/* Secondary Action Button - Optional destructive action (Delete) */}
      {/* Only shows if hideSecondary=false AND handler provided */}
      {!hideSecondary && onSecondaryAction && (
        <Button
          clickHandler={onSecondaryAction}
          className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
          {secondaryLabel}
        </Button>
      )}
      {/* Close Button - Always visible, exits the form */}
      <Button
        clickHandler={onClose}
        className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
        {closeLabel}
      </Button>
    </div>
  );
};

BatchFormActionsComponent.propTypes = {
  onPrimaryAction: PropTypes.func,
  primaryLabel: PropTypes.string,
  onSecondaryAction: PropTypes.func,
  secondaryLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  closeLabel: PropTypes.string,
  hidePrimary: PropTypes.bool,
  hideSecondary: PropTypes.bool
};

export const BatchFormActions = memo(BatchFormActionsComponent);
